import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { PARTNER_TYPES, accentColorForType } from 'common/src/other/PartnerUtils';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Stack,
  Switch, FormControlLabel, Divider, Chip, Avatar, IconButton, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';

const EMPTY = {
  partnerType: PARTNER_TYPES.HOTEL,
  owner: '',
  ownerEmail: '',
  businessName: '',
  legalName: '',
  taxId: '',
  phone: '',
  description: '',
  address: { line1: '', city: '', state: '', country: '', postalCode: '' },
  location: { lat: 0, lng: 0 },
  zoneId: '',
  currency: 'USD',
  amenities: { wifi: false, parking: false, pool: false, ac: false, breakfast: false },
  policies: { checkIn: '14:00', checkOut: '12:00', cancellation: 'flexible' },
  hours: {},
  serviceFee: 0,
  commissionFlat: 0,
  approved: false,
  active: true,
  images: { cover: null, gallery: {} },
};

export default function EditPartner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const zonesdata = useSelector((s) => s.zonesdata);

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const { editPartner, fetchPartners, fetchZones } = api;

  useEffect(() => {
    if (!partnerdata.partners) dispatch(fetchPartners());
    if (!zonesdata.zones) dispatch(fetchZones());
  }, [dispatch, partnerdata.partners, zonesdata.zones, fetchPartners, fetchZones]);

  useEffect(() => {
    if (!isNew && partnerdata.partners) {
      const found = partnerdata.partners.find((p) => p.id === id);
      if (found) {
        setForm({ ...EMPTY, ...found, address: { ...EMPTY.address, ...(found.address || {}) }, location: { ...EMPTY.location, ...(found.location || {}) } });
        setCoverPreview(found.images?.cover || null);
      }
    }
  }, [id, isNew, partnerdata.partners]);

  const update = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i += 1) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const onCoverChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      let savedId = form.id;
      if (isNew) {
        const payload = { ...form, owner: form.owner || auth.profile?.uid };
        savedId = await dispatch(editPartner(payload, 'Add'));
      } else {
        await dispatch(editPartner(form, 'Update'));
      }
      if (coverFile && savedId) {
        await dispatch(editPartner({ id: savedId, coverFile }, 'UpdateCover'));
      }
      navigate('/admin/partners');
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && partnerdata.loading && !partnerdata.partners) return <CircularLoading />;

  return (
    <Box sx={{ p: 2, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/partners')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isNew ? (t('add_partner') || 'Nuevo socio') : (t('edit_partner') || 'Editar socio')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={form.partnerType}
          sx={{
            backgroundColor: `${accentColorForType(form.partnerType)}1a`,
            color: accentColorForType(form.partnerType),
            fontWeight: 600,
          }}
        />
      </Stack>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={1}>
              <Avatar
                src={coverPreview || undefined}
                variant="rounded"
                sx={{ width: 200, height: 150 }}
              >
                {(form.businessName || '?').slice(0, 1)}
              </Avatar>
              <Button component="label" startIcon={<PhotoCameraIcon />} variant="outlined" size="small">
                {t('upload_cover') || 'Subir portada'}
                <input hidden type="file" accept="image/*" onChange={onCoverChange} />
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small"
                  label={t('partner_type') || 'Tipo'}
                  value={form.partnerType}
                  onChange={(e) => update('partnerType', e.target.value)}
                >
                  {Object.values(PARTNER_TYPES).map((pt) => (
                    <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small" label={t('zone') || 'Zona'}
                  select
                  value={form.zoneId || ''}
                  onChange={(e) => update('zoneId', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {(zonesdata.zones || []).map((z) => (
                    <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('business_name') || 'Nombre comercial'}
                  value={form.businessName} onChange={(e) => update('businessName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('legal_name') || 'Razón social'}
                  value={form.legalName} onChange={(e) => update('legalName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('tax_id') || 'RFC/RUT/CUIT'}
                  value={form.taxId} onChange={(e) => update('taxId', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('phone') || 'Teléfono'}
                  value={form.phone} onChange={(e) => update('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('owner_uid') || 'Owner UID'}
                  value={form.owner} onChange={(e) => update('owner', e.target.value)}
                  helperText={isNew ? (t('owner_uid_hint') || 'Si vacío, se asigna al admin actual') : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('owner_email') || 'Email del socio'}
                  value={form.ownerEmail} onChange={(e) => update('ownerEmail', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('description') || 'Descripción'}
                  multiline rows={2} value={form.description}
                  onChange={(e) => update('description', e.target.value)} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
          <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('address') || 'Dirección'}</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label={t('line1') || 'Calle y número'} value={form.address.line1}
              onChange={(e) => update('address.line1', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label={t('city') || 'Ciudad'} value={form.address.city}
              onChange={(e) => update('address.city', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label={t('state') || 'Estado'} value={form.address.state}
              onChange={(e) => update('address.state', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label={t('country') || 'País'} value={form.address.country}
              onChange={(e) => update('address.country', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label={t('postal_code') || 'CP'} value={form.address.postalCode}
              onChange={(e) => update('address.postalCode', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Latitud" type="number" value={form.location.lat}
              onChange={(e) => update('location.lat', parseFloat(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Longitud" type="number" value={form.location.lng}
              onChange={(e) => update('location.lng', parseFloat(e.target.value) || 0)} />
          </Grid>

          {(form.partnerType === PARTNER_TYPES.HOTEL || form.partnerType === PARTNER_TYPES.MOTEL) && (
            <>
              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('amenities') || 'Comodidades'}</Typography>
              </Grid>
              {Object.keys(form.amenities).map((k) => (
                <Grid key={k} item xs={6} sm={2}>
                  <FormControlLabel
                    control={<Switch checked={!!form.amenities[k]} onChange={() => update(`amenities.${k}`, !form.amenities[k])} />}
                    label={t(k) || k}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label={t('check_in') || 'Check-in'} value={form.policies.checkIn}
                  onChange={(e) => update('policies.checkIn', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label={t('check_out') || 'Check-out'} value={form.policies.checkOut}
                  onChange={(e) => update('policies.checkOut', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth size="small" label={t('cancellation') || 'Cancelación'}
                  value={form.policies.cancellation} onChange={(e) => update('policies.cancellation', e.target.value)}>
                  <MenuItem value="flexible">flexible</MenuItem>
                  <MenuItem value="moderate">moderate</MenuItem>
                  <MenuItem value="strict">strict</MenuItem>
                </TextField>
              </Grid>
            </>
          )}

          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label={t('currency') || 'Moneda'}
              value={form.currency} onChange={(e) => update('currency', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="number" label={t('service_fee_pct') || 'Service fee %'}
              value={form.serviceFee} onChange={(e) => update('serviceFee', parseFloat(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="number" label={t('commission_flat') || 'Comisión fija'}
              value={form.commissionFlat} onChange={(e) => update('commissionFlat', parseFloat(e.target.value) || 0)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Switch checked={!!form.approved} onChange={() => update('approved', !form.approved)} />}
                label={t('approved') || 'Aprobado'}
              />
              <FormControlLabel
                control={<Switch checked={form.active !== false} onChange={() => update('active', !form.active)} />}
                label={t('active') || 'Activo'}
              />
            </Stack>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button onClick={() => navigate('/admin/partners')}>{t('cancel') || 'Cancelar'}</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: MAIN_COLOR }}
            onClick={onSave}
            disabled={saving || !form.businessName || !form.partnerType}
          >
            {saving ? '...' : (t('save') || 'Guardar')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
